import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import PageNav from '../components/PageNav'
import { senderOf, to } from '../lib/nav'

import {
  ApiError,
  type Worksheet,
  type WorksheetField,
  type WorksheetSaved,
  appendPhrase,
  getWorksheet,
  saveWorksheet,
} from '../lib/api'

// Generic modality worksheet — editable.
//
// One page for every legacy worksheet (`ABIReport.aspx`, `XRay.aspx`,
// `LEAUltrasound.aspx`, …). The groups, labels, hints and ordering are
// transcribed from the ASPX markup and served as data by
// `GET /api/v1/studies/{id}/worksheet/{modality}`; nothing about a particular
// modality is known here. Editing follows the same shape: the form is keyed by
// the legacy column name and `PATCH …/worksheet/{modality}` takes a partial
// body of exactly the columns the user changed.
//
// Two legacy behaviours deliberately NOT reproduced:
//   * the worksheet saves on page load and regenerates a PDF. Opening a study
//     here reads and nothing else; nothing is written until Save.
//   * it rewrites every column on every save. Only changed columns are sent,
//     so an unedited field cannot be clobbered by a visit.
//
// Every column is free text in the legacy — the velocity boxes hold `---`,
// `N/A` and `>317` — so no numeric input types and no validation are imposed
// here that the legacy did not have.

// What the server renders for the `9999-12-31` / `12319999` "not dictated"
// sentinel that sits on 357,187 rows. It is a rendering, not a stored value:
// it shows as placeholder text rather than input content, so it can never be
// written back as the literal string, and an untouched field stays sentinel.
const SENTINEL = 'not recorded'

type Draft = Record<string, string>

function fieldsOf(sheet: Worksheet): WorksheetField[] {
  return sheet.groups.flatMap((g) => g.fields)
}

function draftFrom(sheet: Worksheet): Draft {
  const d: Draft = {}
  for (const f of fieldsOf(sheet)) {
    d[f.column] = f.value === null || f.value === SENTINEL ? '' : f.value
  }
  return d
}

// Only what the user actually changed, compared the way the server stores it:
// blank and NULL are one value.
function diff(base: Draft, draft: Draft) {
  const patch: Record<string, string | null> = {}
  for (const column of Object.keys(draft)) {
    const now = draft[column].trim()
    if (now !== (base[column] ?? '').trim()) patch[column] = now === '' ? null : now
  }
  return patch
}

export default function WorksheetPage() {
  const { studyId, modality } = useParams()
  const id = Number(studyId)
  const [params] = useSearchParams()
  const navigate = useNavigate()

  const [sheet, setSheet] = useState<Worksheet | null>(null)
  const [base, setBase] = useState<Draft | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)

  const [error, setError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveStatus, setSaveStatus] = useState<number | null>(null)
  const [result, setResult] = useState<WorksheetSaved | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    const s = await getWorksheet(id, String(modality))
    setSheet(s)
    setBase(draftFrom(s))
    setDraft(draftFrom(s))
  }, [id, modality])

  useEffect(() => {
    if (!Number.isFinite(id)) return
    setSheet(null)
    setBase(null)
    setDraft(null)
    setError(null)
    setSaveError(null)
    setResult(null)
    load().catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [id, load])

  const pending = useMemo(
    () => (base && draft ? diff(base, draft) : {}),
    [base, draft],
  )
  const dirty = Object.keys(pending).length > 0

  // The browser-level half of the guard: reload, close, and navigation out of
  // the app. In-app links go through `leave()` below, because this router is a
  // `<BrowserRouter>` and `useBlocker` needs a data router.
  useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  // Where Generate Report and Cancel go.
  //
  // `ABIReport.aspx.vb:779` and `ABIReportUpdate.aspx.vb:791` — every worksheet
  // ends `btnGenerateReport_Click` with
  // `Response.Redirect("StudyReport.aspx?STUDYID=" & ViewState("STUDYID"))`,
  // and the `*Update` twins send Cancel to the same page (`:794`). The hub is
  // where the report you just generated is, so that is where saving lands. Only
  // the non-Update originals cancel to Study Information
  // (`ABIReport.aspx.vb:502`), and those are reached from Study Information in
  // the first place — which is exactly what `?sender=` says here.
  const exit = senderOf(params, to(`/studies/${studyId}/report`, params))

  function leave(e: { preventDefault: () => void }) {
    if (!dirty) return true
    if (window.confirm('This worksheet has unsaved changes. Leave without saving?')) return true
    e.preventDefault()
    return false
  }

  function set(column: string, value: string) {
    setDraft((d) => (d ? { ...d, [column]: value } : d))
    setResult(null)
    setSaveError(null)
    setSaveStatus(null)
  }

  async function save(andContinue: boolean) {
    if (!base || !draft) return
    setSaving(true)
    setSaveError(null)
    setSaveStatus(null)
    setResult(null)
    try {
      const res = await saveWorksheet(id, String(modality), diff(base, draft))
      if (andContinue) {
        navigate(exit)
        return
      }
      setResult(res)
      // The write is already committed; a failed refresh must not be reported
      // as a failed save.
      try {
        await load()
      } catch {
        /* the page keeps showing the pre-save read until the next visit */
      }
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : String(e))
      setSaveStatus(e instanceof ApiError ? e.status : null)
    } finally {
      setSaving(false)
    }
  }

  const header = <PageNav strip="worksheet" studyId={studyId} />

  if (!Number.isFinite(id)) {
    return <main className="page"><p className="banner banner-error">Invalid study id.</p></main>
  }
  if (error) {
    return (
      <main className="page page-wide">
        {header}
        <p className="banner banner-error">{error}</p>
      </main>
    )
  }
  if (!sheet || !base || !draft) {
    return <main className="page"><p className="muted">Loading…</p></main>
  }

  const labels: Record<string, string> = {}
  for (const f of fieldsOf(sheet)) labels[f.column] = f.label

  return (
    <main className="page page-wide">
      {header}

      <div className="page-head">
        <h1>{sheet.title}</h1>
        <p className="subtle">
          {sheet.legacy_page} · <code>{sheet.table}</code>
          {(() => {
            // "every field is free text" was true of the replacement and never
            // of the legacy: 227 of these columns are dropdowns over a fixed
            // vocabulary. Count what this worksheet actually offers.
            const all = sheet.groups.flatMap((g) => g.fields)
            const lists = all.filter((f) => f.kind === 'select').length
            return lists
              ? ` · ${lists} of ${all.length} fields choose from a list`
              : ' · every field is free text'
          })()}
        </p>
      </div>

      {saveError && (
        <p className="banner banner-error">
          <strong>Not saved.</strong> {saveError}
          {saveStatus === 503 && ' Writes to the legacy database are disabled; nothing was changed.'}
        </p>
      )}
      {result && <SaveSummary result={result} labels={labels} table={sheet.table} />}

      {/* The per-modality transcription note used to be drawn here as a warning
          banner. Removed by product direction (14 August 2026): it addressed
          whoever was reproducing the legacy, not the clinician filling the form,
          and a standing amber banner above every worksheet trains people to
          ignore the banner that means something. The findings themselves are in
          `docs/worksheet-findings.md`. */}

      {!sheet.exists && (
        <p className="banner banner-warn">
          No <code>{sheet.table}</code> row for study {studyId}. The
          legacy writes this row on first open of the worksheet; here the form is
          blank until the first save creates it.
        </p>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          void save(false)
        }}
      >
        {/* One fieldset per legacy fieldset/section, in the order the page prints
            them. `.cols` supplies the fieldset styling; the groups stack because a
            measurement grid is unreadable in a narrow column. */}
        <div className="cols" style={{ gridTemplateColumns: '1fr' }}>
          {sheet.groups.map((group) => (
            <fieldset key={group.title}>
              <legend>{group.title}</legend>
              <div
                style={
                  group.columns > 1
                    ? {
                        display: 'grid',
                        gridTemplateColumns: `repeat(auto-fit, minmax(${
                          group.columns >= 3 ? '17rem' : '22rem'
                        }, 1fr))`,
                        columnGap: '1.5rem',
                      }
                    : undefined
                }
              >
                {group.fields.map((field) => (
                  <FieldRow
                    key={field.column}
                    field={field}
                    value={draft[field.column] ?? ''}
                    changed={field.column in pending}
                    disabled={saving}
                    onChange={(v) => set(field.column, v)}
                  />
                ))}
              </div>
            </fieldset>
          ))}
        </div>

        <div className="actions ws-actions">
          <button type="submit" disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {/* Enabled even when clean: an empty patch writes nothing, so this
              stays a "save and move on" whether or not anything changed. */}
          <button type="button" disabled={saving} onClick={() => void save(true)}>
            Save and continue
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={(e) => {
              if (leave(e)) navigate(exit)
            }}
          >
            Cancel
          </button>
          <span className="subtle">
            {dirty
              ? `Unsaved: ${Object.keys(pending)
                  .map((c) => labels[c] ?? c)
                  .join(', ')}`
              : 'No changes. Nothing is written until a field changes.'}
          </span>
        </div>
      </form>
    </main>
  )
}

// What actually changed, in the user's words rather than column names.
function SaveSummary({
  result, labels, table,
}: {
  result: WorksheetSaved
  labels: Record<string, string>
  table: string
}) {
  const show = (v: string | number | null) =>
    v === null || v === '' ? '—' : String(v)

  const rows = Object.entries(result.changed)
  if (rows.length === 0) {
    return <p className="banner banner-ok">Nothing to save — the worksheet is unchanged.</p>
  }

  return (
    <div className="banner banner-ok">
      <strong>Saved.</strong>
      {result.created && ` Created the ${table} row for this study.`}
      <ul className="save-summary">
        {rows.map(([column, change]) => (
          <li key={column}>
            {labels[column] ?? column}: {show(change.from)} →{' '}
            <strong>{show(change.to)}</strong>
          </li>
        ))}
      </ul>
    </div>
  )
}

function FieldRow({
  field, value, changed, disabled, onChange,
}: {
  field: WorksheetField
  value: string
  changed: boolean
  disabled: boolean
  onChange: (value: string) => void
}) {
  const inputId = `ws-${field.column}`
  // The phrase picker's own selection. Deliberately not part of the draft: the
  // legacy's dropdown is a way of choosing what to append and is never saved.
  const [picked, setPicked] = useState('')
  // A sentinel date has no editable content: the words stay visible as
  // placeholder, and an untouched field sends nothing, so the sentinel survives.
  const sentinel = field.value === SENTINEL
  const label = (
    <label className="ro-label" htmlFor={inputId} title={field.column}>
      {field.label}
      {field.hint && <span className="muted">{` ${field.hint}`}</span>}
    </label>
  )
  const cls = `ro-field ws-field${changed ? ' changed' : ''}`

  // A bit column the legacy drew as a checkbox. The save path accepts
  // 'True'/'False' for a bit and renders them back the same way, so the value
  // stays a string end to end and nothing else has to know this is a boolean.
  if (field.kind === 'checkbox') {
    const checked = value === 'True'
    return (
      <div className={cls}>
        {label}
        <input
          id={inputId}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked ? 'True' : 'False')}
        />
      </div>
    )
  }

  // A dropdown the legacy drew as an `<asp:dropdownlist>`. The options are not
  // decoration: the report prints the chosen phrase into a sentence — "The
  // calculated ABI [is abnormal.] The Findings [suggest] a [moderate] risk of
  // significant P.A.D." — so free text here produces a sentence that does not
  // read. A stored value outside the list is kept and shown, because refusing
  // to display what is in the database would hide it.
  if (field.kind === 'select' && field.choices?.length) {
    const options = field.choices.includes(value) || !value
      ? field.choices
      : [value, ...field.choices]
    // Eleven of these hold a whole sentence — "The jugular, subclavian,
    // axillary, cephalic, brachial, and basilic veins are patent with normal
    // flow." — and need the width a narrative field gets.
    const style = field.wide
      ? { gridColumn: '1 / -1', gridTemplateColumns: '1fr' }
      : undefined
    return (
      <div className={cls} style={style}>
        {label}
        <select
          id={inputId}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="" />
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>
    )
  }

  // A comment box with a phrase library beside it — the echo/TEE Add buttons.
  // The legacy pairs each of nine comment textboxes with a dropdown of standard
  // sentences and an image button that appends the chosen one
  // (`btnAddPericardiumComment_Click` → `BuildComments`). The box stays freely
  // editable either way: the phrase is a starting point, not a value.
  //
  // Appending rather than replacing is the whole point. A clinician builds
  // "Dilated LV  Trace MR  Mild LVH" by picking three times, which is why this
  // cannot be a `select`.
  if (field.phrases?.length) {
    return (
      <div className={cls} style={{ gridColumn: '1 / -1', gridTemplateColumns: '1fr' }}>
        {label}
        <div className="ws-phrase">
          <textarea
            id={inputId}
            rows={3}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
          <div className="ws-phrase-pick">
            <select
              aria-label={`Standard phrases for ${field.label}`}
              value={picked}
              disabled={disabled}
              onChange={(e) => setPicked(e.target.value)}
            >
              <option value="">Standard phrase…</option>
              {field.phrases.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={disabled || !picked}
              onClick={() => {
                onChange(appendPhrase(value, picked))
                setPicked('')
              }}
            >
              Add
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Narrative and filename fields span the whole grid; a paragraph of
  // impression text in a 17rem column is unreadable.
  if (field.wide) {
    return (
      <div className={cls} style={{ gridColumn: '1 / -1', gridTemplateColumns: '1fr' }}>
        {label}
        <textarea
          id={inputId}
          rows={3}
          value={value}
          disabled={disabled}
          placeholder={sentinel ? SENTINEL : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    )
  }

  return (
    <div className={cls}>
      {label}
      <input
        id={inputId}
        type="text"
        value={value}
        disabled={disabled}
        placeholder={sentinel ? SENTINEL : undefined}
        onChange={(e) => onChange(e.target.value)}
        /* A measurement or a code — "2.5", "RCA", "80-99%". On a phone iOS
           capitalises the first character and autocorrects the rest, which on
           this field can only change a value for the worse. The narrative
           textarea above keeps both: correction is wanted in prose. */
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
      />
    </div>
  )
}
